import type { PanderrSection } from "../types";
import { renderPanderrButton } from "../components/button";

function escapeHtml(value: string): string {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

export function renderMunicipalHomeSection(
  section: PanderrSection,
): string {
  const buttons = section.buttons ?? [];
  const services = section.services ?? [];
  const projects = section.projects ?? [];
  const steps = section.steps ?? [];
  const image = section.images?.[0];

  return `
    <section
      class="panderr-municipal-home"
      data-panderr-section="municipal-home"
      data-panderr-section-id="${escapeHtml(section.id)}"
    >
      <div class="panderr-municipal-hero">
        <div class="panderr-shell-wide panderr-municipal-hero-grid">
          <div class="panderr-municipal-hero-copy">
            ${
              section.eyebrow
                ? `<p class="panderr-municipal-eyebrow">${escapeHtml(section.eyebrow)}</p>`
                : ""
            }

            <h1>${escapeHtml(section.heading || "")}</h1>

            ${
              section.body
                ? `<p class="panderr-municipal-lead">${escapeHtml(section.body)}</p>`
                : ""
            }

            ${
              buttons.length
                ? `
                  <div class="panderr-municipal-actions">
                    ${buttons.map(renderPanderrButton).join("")}
                  </div>
                `
                : ""
            }
          </div>

          ${
            image
              ? `
                <div class="panderr-municipal-hero-media">
                  <img
                    src="${escapeHtml(image.src)}"
                    alt="${escapeHtml(image.alt)}"
                  >
                </div>
              `
              : ""
          }
        </div>
      </div>

      ${
        services.length
          ? `
            <div class="panderr-municipal-quick-wrap">
              <div class="panderr-shell-wide">
                <div class="panderr-municipal-quick-grid">
                  ${services
                    .map(
                      (item) => `
                        <a
                          class="panderr-municipal-quick-card"
                          href="${escapeHtml(item.href || "#")}"
                        >
                          <span class="panderr-municipal-quick-title">
                            ${escapeHtml(item.title)}
                          </span>
                          <span class="panderr-municipal-quick-description">
                            ${escapeHtml(item.description)}
                          </span>
                          <span class="panderr-municipal-quick-link">
                            View information →
                          </span>
                        </a>
                      `,
                    )
                    .join("")}
                </div>
              </div>
            </div>
          `
          : ""
      }

      <div class="panderr-shell-wide panderr-municipal-content">
        ${
          projects.length
            ? `
              <section class="panderr-municipal-section">
                <p class="panderr-municipal-eyebrow">
                  Community
                </p>
                <h2>Small town values. A connected community.</h2>

                <div class="panderr-municipal-feature-grid">
                  ${projects
                    .map(
                      (item) => `
                        <article class="panderr-municipal-feature">
                          ${
                            item.image
                              ? `
                                <img
                                  src="${escapeHtml(item.image.src)}"
                                  alt="${escapeHtml(item.image.alt)}"
                                >
                              `
                              : ""
                          }
                          <div class="panderr-municipal-feature-copy">
                            <h3>${escapeHtml(item.title)}</h3>
                            ${
                              item.description
                                ? `<p>${escapeHtml(item.description)}</p>`
                                : ""
                            }
                            ${
                              item.href
                                ? `<a href="${escapeHtml(item.href)}">Learn more →</a>`
                                : ""
                            }
                          </div>
                        </article>
                      `,
                    )
                    .join("")}
                </div>
              </section>
            `
            : ""
        }

        ${
          steps.length
            ? `
              <section class="panderr-municipal-section panderr-municipal-news">
                <div class="panderr-municipal-section-heading">
                  <div>
                    <p class="panderr-municipal-eyebrow">
                      Stay informed
                    </p>
                    <h2>Latest news & public information</h2>
                  </div>

                  <a href="/news-events/" class="panderr-municipal-text-link">
                    View News & Events →
                  </a>
                </div>

                <div class="panderr-municipal-news-grid">
                  ${steps
                    .map(
                      (item) => `
                        <article class="panderr-municipal-news-card">
                          <span class="panderr-municipal-news-label">
                            Town Update
                          </span>
                          <h3>${escapeHtml(item.title)}</h3>
                          <p>${escapeHtml(item.description)}</p>
                        </article>
                      `,
                    )
                    .join("")}
                </div>
              </section>
            `
            : ""
        }

        <section class="panderr-municipal-government-callout">
          <div>
            <p class="panderr-municipal-eyebrow">
              Your local government
            </p>
            <h2>Public information should be easy to find.</h2>
            <p>
              Access elected-official information, meeting records,
              budgets, public notices, open-records resources, and
              municipal administration information in one place.
            </p>
          </div>

          <div class="panderr-municipal-government-links">
            <a href="/government/mayor-council/">Mayor & Council</a>
            <a href="/government/agendas-minutes/">Agendas & Minutes</a>
            <a href="/government/budgets-projects/">Budgets & Projects</a>
            <a href="/government/public-notices/">Public Notices</a>
            <a href="/government/open-records/">Open Records</a>
          </div>
        </section>
      </div>
    </section>
  `;
}

export function createMunicipalHomeCss(): string {
  return `
    body[data-panderr-template-id="municipality-modern"] .panderr-municipal-home {
      --municipal-navy: #173f69;
      --municipal-dark-navy: #102f50;
      --municipal-soft-blue: #eef4f8;
      --municipal-gold: #b99a55;
      --municipal-border: #dce3e8;
      --municipal-text: #263746;
      --municipal-muted: #667583;
      background: #fff;
      color: var(--municipal-text);
    }

    .panderr-municipal-hero {
      background:
        linear-gradient(
          105deg,
          var(--municipal-dark-navy) 0%,
          var(--municipal-navy) 58%,
          #24577f 100%
        );
      color: #fff;
      padding: clamp(4.5rem, 8vw, 7.5rem) 0
        clamp(6rem, 10vw, 9rem);
    }

    .panderr-municipal-hero-grid {
      display: grid;
      grid-template-columns:
        minmax(0, 0.95fr)
        minmax(0, 1.05fr);
      gap: clamp(3rem, 7vw, 7rem);
      align-items: center;
    }

    .panderr-municipal-hero-copy {
      max-width: 720px;
    }

    .panderr-municipal-eyebrow {
      margin: 0 0 1rem;
      font-size: 0.78rem;
      font-weight: 800;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--municipal-gold);
    }

    .panderr-municipal-hero h1 {
      margin: 0;
      max-width: 11ch;
      font-size: clamp(3rem, 6vw, 5.75rem);
      line-height: 0.98;
      letter-spacing: -0.045em;
      color: #fff;
    }

    .panderr-municipal-lead {
      margin: 1.5rem 0 0;
      max-width: 58ch;
      font-size: clamp(1.05rem, 1.5vw, 1.25rem);
      line-height: 1.7;
      color: rgba(255, 255, 255, 0.84);
    }

    .panderr-municipal-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 0.9rem;
      margin-top: 2rem;
    }

    .panderr-municipal-hero-media {
      overflow: hidden;
      min-height: 420px;
      border-radius: 1.25rem;
      box-shadow: 0 24px 70px rgba(0, 0, 0, 0.24);
      background: rgba(255, 255, 255, 0.08);
    }

    .panderr-municipal-hero-media img {
      display: block;
      width: 100%;
      height: 100%;
      min-height: 420px;
      object-fit: cover;
    }

    .panderr-municipal-quick-wrap {
      margin-top: -3.5rem;
      position: relative;
      z-index: 2;
    }

    .panderr-municipal-quick-grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 1rem;
    }

    .panderr-municipal-quick-card {
      display: flex;
      min-height: 190px;
      flex-direction: column;
      padding: 1.6rem;
      text-decoration: none;
      color: var(--municipal-text);
      background: #fff;
      border: 1px solid var(--municipal-border);
      border-radius: 1rem;
      box-shadow: 0 12px 35px rgba(16, 47, 80, 0.09);
      transition:
        transform 160ms ease,
        box-shadow 160ms ease,
        border-color 160ms ease;
    }

    .panderr-municipal-quick-card:hover {
      transform: translateY(-4px);
      border-color: #b9c8d3;
      box-shadow: 0 18px 45px rgba(16, 47, 80, 0.14);
    }

    .panderr-municipal-quick-title {
      font-size: 1.08rem;
      font-weight: 800;
      color: var(--municipal-dark-navy);
    }

    .panderr-municipal-quick-description {
      margin-top: 0.8rem;
      line-height: 1.6;
      color: var(--municipal-muted);
    }

    .panderr-municipal-quick-link {
      margin-top: auto;
      padding-top: 1rem;
      font-size: 0.9rem;
      font-weight: 750;
      color: var(--municipal-navy);
    }

    .panderr-municipal-content {
      padding-top: clamp(5rem, 9vw, 8rem);
      padding-bottom: clamp(5rem, 9vw, 8rem);
    }

    .panderr-municipal-section + .panderr-municipal-section {
      margin-top: clamp(5rem, 9vw, 8rem);
    }

    .panderr-municipal-section h2,
    .panderr-municipal-government-callout h2 {
      margin: 0;
      max-width: 16ch;
      font-size: clamp(2.2rem, 4vw, 4rem);
      line-height: 1.05;
      letter-spacing: -0.035em;
      color: var(--municipal-dark-navy);
    }

    .panderr-municipal-feature-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 1.5rem;
      margin-top: 2.5rem;
    }

    .panderr-municipal-feature {
      overflow: hidden;
      background: #fff;
      border: 1px solid var(--municipal-border);
      border-radius: 1rem;
    }

    .panderr-municipal-feature img {
      display: block;
      width: 100%;
      height: 290px;
      object-fit: cover;
    }

    .panderr-municipal-feature-copy {
      padding: 1.6rem;
    }

    .panderr-municipal-feature-copy h3,
    .panderr-municipal-news-card h3 {
      margin: 0;
      color: var(--municipal-dark-navy);
    }

    .panderr-municipal-feature-copy p,
    .panderr-municipal-news-card p,
    .panderr-municipal-government-callout p {
      color: var(--municipal-muted);
      line-height: 1.7;
    }

    .panderr-municipal-feature-copy a,
    .panderr-municipal-text-link {
      font-weight: 750;
      color: var(--municipal-navy);
      text-decoration: none;
    }

    .panderr-municipal-section-heading {
      display: flex;
      justify-content: space-between;
      gap: 2rem;
      align-items: end;
    }

    .panderr-municipal-news-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 1rem;
      margin-top: 2.5rem;
    }

    .panderr-municipal-news-card {
      padding: 1.6rem;
      border: 1px solid var(--municipal-border);
      border-radius: 1rem;
      background: var(--municipal-soft-blue);
    }

    .panderr-municipal-news-label {
      display: inline-block;
      margin-bottom: 1rem;
      font-size: 0.74rem;
      font-weight: 800;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--municipal-navy);
    }

    .panderr-municipal-government-callout {
      display: grid;
      grid-template-columns:
        minmax(0, 1.15fr)
        minmax(280px, 0.85fr);
      gap: clamp(2.5rem, 6vw, 6rem);
      margin-top: clamp(5rem, 9vw, 8rem);
      padding: clamp(2rem, 5vw, 4rem);
      border-radius: 1.25rem;
      background: var(--municipal-soft-blue);
    }

    .panderr-municipal-government-links {
      display: grid;
      align-content: center;
    }

    .panderr-municipal-government-links a {
      padding: 0.9rem 0;
      color: var(--municipal-dark-navy);
      font-weight: 750;
      text-decoration: none;
      border-bottom: 1px solid #cbd9e3;
    }

    @media (max-width: 1000px) {
      .panderr-municipal-hero-grid {
        grid-template-columns: 1fr;
      }

      .panderr-municipal-hero h1 {
        max-width: 14ch;
      }

      .panderr-municipal-quick-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      .panderr-municipal-news-grid {
        grid-template-columns: 1fr;
      }
    }

    @media (max-width: 760px) {
      .panderr-municipal-quick-wrap {
        margin-top: -2rem;
      }

      .panderr-municipal-quick-grid,
      .panderr-municipal-feature-grid,
      .panderr-municipal-government-callout {
        grid-template-columns: 1fr;
      }

      .panderr-municipal-section-heading {
        align-items: flex-start;
        flex-direction: column;
      }

      .panderr-municipal-hero-media,
      .panderr-municipal-hero-media img {
        min-height: 300px;
      }
    }

    /*
     * Municipality Modern — polished civic hero.
     */
    body[data-panderr-template-id="municipality-modern"]
      .panderr-municipal-hero {
      padding-top: clamp(4rem, 6vw, 6rem);
      padding-bottom: clamp(5.5rem, 7vw, 7rem);
    }

    body[data-panderr-template-id="municipality-modern"]
      .panderr-municipal-hero
      .panderr-button-secondary {
      color: #fff;
      border-color: rgba(255, 255, 255, 0.82);
      background: transparent;
    }

    body[data-panderr-template-id="municipality-modern"]
      .panderr-municipal-hero
      .panderr-button-secondary:hover {
      color: #102f50;
      border-color: #fff;
      background: #fff;
    }

    /*
     * Municipality Modern — no-photo civic hero.
     * When no legitimate municipal image is available, the homepage
     * intentionally uses a strong civic presentation rather than
     * representing stock photography as the community.
     */
    body[data-panderr-template-id="municipality-modern"]
      .panderr-municipal-hero-grid:not(:has(.panderr-municipal-hero-media)) {
      grid-template-columns: minmax(0, 920px);
    }

    body[data-panderr-template-id="municipality-modern"]
      .panderr-municipal-hero-grid:not(:has(.panderr-municipal-hero-media))
      .panderr-municipal-hero-copy {
      max-width: 920px;
    }

    body[data-panderr-template-id="municipality-modern"]
      .panderr-municipal-hero-grid:not(:has(.panderr-municipal-hero-media))
      .panderr-municipal-hero h1 {
      max-width: 13ch;
      font-size: clamp(3rem, 5.2vw, 5rem);
    }

  `;
}
